import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tabs, Tab } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const Section6 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Retrieve initial type from the URL; default to "1" if not present
  const queryParams = new URLSearchParams(location.search);
  const initialType = queryParams.get('type') || '1';

  // Local state for the selected tab (for navigation) and displayed tab (for animation)
  const [selectedTab, setSelectedTab] = useState(initialType);
  const [displayedTab, setDisplayedTab] = useState(initialType);

  // Animation stage: 'fade-in', 'fade-out', or 'pre-slide'
  const [animationClass, setAnimationClass] = useState('fade-in');

  // Sync local state with URL param if it changes externally
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeFromUrl = params.get('type') || '1';
    if (typeFromUrl !== selectedTab) {
      setSelectedTab(typeFromUrl);
    }
  }, [location.search, selectedTab]);

  // Animate fade/slide when tab changes
  useEffect(() => {
    if (selectedTab !== displayedTab) {
      // Step 1: Fade-out and slide out to the left
      setAnimationClass('fade-out');
      const fadeOutTimer = setTimeout(() => {
        // Step 2: Update content and position it off-screen right
        setDisplayedTab(selectedTab);
        setAnimationClass('pre-slide');
        // Step 3: Trigger fade-in and slide in from the right
        const slideInTimer = setTimeout(() => {
          setAnimationClass('fade-in');
        }, 50); // A short delay to allow the browser to apply the "pre-slide" state
        return () => clearTimeout(slideInTimer);
      }, 300); // Matches the fade-out transition duration
      return () => clearTimeout(fadeOutTimer);
    }
  }, [selectedTab, displayedTab]);

  // Update URL and local state on tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    navigate(`?type=${newValue}`);
  };

  const handleScroll = (id) => {
    navigate(`/`);
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        window.scrollTo({
          top: element.offsetTop - 80,
          behavior: 'smooth',
        });
      }
    }, 500);
  };

  const tabSx = {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.9rem',
    color: 'text.secondary',
    '&.Mui-selected': {
      color: '#04aee7',
      fontWeight: 600,
    },
    '&:hover': {
      color: '#04aee7',
      fontWeight: 600,
    },
    minWidth: isMobile ? 'auto' : 'inherit',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        marginTop: '10vh',
        paddingBottom: '100px',
      }}
    >
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        centered={!isMobile}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={isMobile ? 'auto' : 'off'}
        sx={{
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          marginBottom: '2rem',
          '.MuiTabs-indicator': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Tab value="1" label={t('service1')} sx={tabSx} />
        <Tab value="2" label={t('service2')} sx={tabSx} />
        <Tab value="3" label={t('service3')} sx={tabSx} />
        <Tab value="4" label={t('service4')} sx={tabSx} />
      </Tabs>

      <div
        className={`fade-container container d-flex ${animationClass}`}
        style={{ paddingTop: '100px', textAlign: 'left' }}
      >
        <div style={{ paddingRight: '50px' }} className="col-md-8 col-12">
          <h1 className="title">
            {t('serviceTop')}{' '}
            <span className="gradient-text">{t('serviceFe')} </span>{' '}
          </h1>
          <h1 style={{ marginTop: '-10px' }} className="title">
            {t('serviceFe2')} {t('service' + displayedTab)}
          </h1>
          <p className="text mt-3">{t('serviceDesc' + displayedTab)}</p>
          <Button
            onClick={() => handleScroll('section2')}
            className="mt-4"
            variant="contained"
            color="primary"
            style={{
              backgroundColor: '#04aee7',
              color: '#FFFFFF',
              borderRadius: '30px',
              padding: '8px 25px',
            }}
          >
            {t('getQuote')}
          </Button>
        </div>
        <div className="col-md-4 col-12" style={{ textAlign: 'right' }}>
          <img
            src={`/img/service${displayedTab}.gif`}
            width={320}
            alt="Service de transport"
          />
        </div>
      </div>

      <div style={{ marginTop: '20vh' }} className="container">
        <div className="row mt-4">
          <div className="col-md-4 col-12" style={{ paddingRight: '50px' }}>
            <div style={{ border: '2px solid black' }} />
            <div style={{ maxWidth: '450px' }}>
              <h4 style={{ maxWidth: '90%' }} className="section-title mt-5">
                {t('optimizedDesign' + displayedTab)}
              </h4>
              <p className="text mt-1">
                {t('leanTrailerPerformance' + displayedTab)}
              </p>
            </div>
          </div>
          <div className="col-md-4 col-12" style={{ paddingRight: '50px' }}>
            {' '}
            <div style={{ border: '1px solid black' }} />
            <div style={{ maxWidth: '450px' }}>
              <h4 style={{ maxWidth: '90%' }} className="section-title mt-5">
                {t('customSolution' + displayedTab)}
              </h4>
              <p className="text mt-1">
                {t('leanTrailerFeatures' + displayedTab)}
              </p>
            </div>
          </div>
          <div className="col-md-4 col-12" style={{ paddingRight: '50px' }}>
            {' '}
            <div style={{ border: '1px solid black' }} />
            <div style={{ maxWidth: '450px' }}>
              <h4 style={{ maxWidth: '90%' }} className="section-title mt-5">
                {t('customTransport' + displayedTab)}
              </h4>
              <p className="text mt-1">
                {t('leanTrailerTransport' + displayedTab)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Section6;
